import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Seo from '../components/Seo';
import Navbar from '../components/site/Navbar';
import Footer from '../components/site/Footer';
import ChatWidget from '../components/site/ChatWidget';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Star, Loader2, ArrowLeft, Upload } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { submitTestimonial } from '../lib/api';
import { useI18n } from '../i18n';

const readAsBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

const SubmitTestimonialPage = () => {
  const nav = useNavigate();
  const { toast } = useToast();
  const { t } = useI18n();
  const [form, setForm] = useState({ name: '', role: '', company: '', country: '', quote: '', rating: 5, avatar: '', logo: '' });
  const [loading, setLoading] = useState(false);

  const setLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1.2 * 1024 * 1024) {
      toast({ title: t('testimonial.tooLarge'), description: t('testimonial.tooLargeBody') });
      return;
    }
    const b64 = await readAsBase64(file);
    setForm((f) => ({ ...f, logo: b64 }));
  };

  const setAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1.2 * 1024 * 1024) {
      toast({ title: t('testimonial.tooLarge'), description: t('testimonial.tooLargeBody') });
      return;
    }
    const b64 = await readAsBase64(file);
    setForm((f) => ({ ...f, avatar: b64 }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || form.quote.length < 10) {
      toast({ title: t('testimonial.almost'), description: t('testimonial.almostBody') });
      return;
    }
    setLoading(true);
    try {
      await submitTestimonial(form);
      toast({ title: t('testimonial.thankTitle'), description: t('testimonial.thankBody') });
      setTimeout(() => nav('/'), 1200);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join(', ') : detail || '';
      toast({ title: t('form.error'), description: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Seo title="Share Your Experience | Hipro Technologies" description="Submit a testimonial about working with Hipro Technologies." path="/submit-testimonial" noindex />
      <Navbar />
      <section className="pt-28 pb-14 bg-slate-950 text-white">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-300 hover:text-orange-400">
            <ArrowLeft className="h-4 w-4" /> {t('common.backHome')}
          </Link>
          <h1 className="mt-4 text-3xl sm:text-4xl font-extrabold">{t('testimonial.hero')}</h1>
          <p className="mt-3 text-slate-300">{t('testimonial.subtitle')}</p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <form onSubmit={submit} className="rounded-2xl border border-slate-200 p-6 sm:p-8 space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>{t('testimonial.yourName')}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 mt-1" /></div>
              <div><Label>{t('testimonial.role')}</Label><Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="h-11 mt-1" /></div>
              <div><Label>{t('testimonial.company')}</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="h-11 mt-1" /></div>
              <div><Label>{t('testimonial.country')}</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="h-11 mt-1" /></div>
            </div>

            <div>
              <Label>{t('testimonial.feedback')}</Label>
              <Textarea rows={5} value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} className="mt-1" placeholder={t('testimonial.feedbackPh')} />
              <div className="mt-1 text-xs text-slate-500">{form.quote.length}/1200</div>
            </div>

            <div>
              <Label>{t('testimonial.rating')}</Label>
              <div className="mt-1 flex items-center gap-1">
                {[1,2,3,4,5].map((n) => (
                  <button type="button" key={n} onClick={() => setForm({ ...form, rating: n })}>
                    <Star className={`h-6 w-6 ${n <= form.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`} />
                  </button>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-2"><Upload className="h-4 w-4" /> {t('testimonial.photo')}</Label>
                <input type="file" accept="image/*" onChange={setAvatar} className="mt-2 block w-full text-sm text-slate-600" />
                {form.avatar && <img src={form.avatar} alt="avatar preview" className="mt-3 h-14 w-14 rounded-full object-cover border" />}
              </div>
              <div>
                <Label className="flex items-center gap-2"><Upload className="h-4 w-4" /> {t('testimonial.logo')}</Label>
                <input type="file" accept="image/*" onChange={setLogo} className="mt-2 block w-full text-sm text-slate-600" />
                {form.logo && <img src={form.logo} alt="logo preview" className="mt-3 h-10 object-contain border rounded px-2 py-1 bg-white" />}
              </div>
            </div>

            <Button type="submit" disabled={loading} className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-semibold">
              {loading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('common.submitting')}</>) : t('testimonial.submit')}
            </Button>
          </form>
        </div>
      </section>

      <Footer />
      <ChatWidget />
    </div>
  );
};

export default SubmitTestimonialPage;
